import {
  PlaylistListApi,
  PlaylistGetApi,
  PlaylistListItem,
  PlaylistCustomItem,
} from "@/database/media/api";
import { GetYoutubePlaylistAPI } from "@/database/user/api";
import { YoutubePlaylist, SocialChannel } from "@/database/user/model";

export type PlaylistNature = "site" | "both";

export interface Playlist {
  id: string;
  name: string;
  nature: PlaylistNature;
  channelId?: string; // the linked YouTube channel (for "both")
  channelName?: string;
  createdBy?: string;
  createdAt?: string; // human label, e.g. "12 May 2026"
  updatedAt?: string; // human label, e.g. "2 days ago"
  mtBy?: string; // last modified by
  mt?: number; // last modified time (epoch)
  videoCount?: number; // total videos in the playlist
  videos?: PlaylistVideo[]; // the playlist's items (from the list response's custom_items)
  youtubeChannels?: {
    channelId?: string;
    channelName: string;
    playlist_name: string;
    playlist_id: string;
    channelCategory?: string;
  }[]; // from youtube_playlist
  denmarkChannels?: {
    channelName: string;
    host?: number;
    section?: number;
    folderPath?: string;
  }[];
}

export interface PlaylistVideo {
  id: string;
  title: string;
  uploader: string;
  publishedAt: string;
  thumb?: string; // video thumbnail URL
  pinned?: boolean;
  isThisVideo?: boolean;
}

// Map one `custom_items` entry into the UI's PlaylistVideo shape. custom_items carry
// { id, title, artist, duration, position, thumbnail } — no publish date, so the row's
// secondary line shows the duration instead.
const toVideo = (item: PlaylistCustomItem): PlaylistVideo => ({
  id: item.id,
  title: item.title || "Untitled",
  uploader: item.artist || "",
  publishedAt: item.duration || "",
  thumb: item.thumbnail || undefined,
});

// Map a raw `playlist.list` row into the UI's Playlist shape. Custom playlists have no linked
// YouTube channel, so they're on-site ("site"); the items come from `custom_items` (ordered by
// their `position`).
export const toPlaylist = (row: PlaylistListItem): Playlist => {
  const items = Array.isArray(row.custom_items) ? row.custom_items : [];
  const videos = [...items]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map(toVideo);
  const youtubeChannels = (row.youtube_playlist || [])
    .filter((m) => m.channel_name || m.channel_id)
    .map((m) => ({
      channelId: m.channel_id,
      channelName: m.channel_name || m.channel_id || "",
      playlist_name: m.playlist_name || "",
      playlist_id: m.playlist_id || "",
      channelCategory: m.channel_category,
    }));
  const denmarkChannels = (row.denmark_playlist || [])
    .filter((m) => m.channel_name || m.section != null)
    .map((m) => ({
      channelName: m.channel_name || String(m.section ?? ""),
      host: m.host,
      section: m.section,
      folderPath: m.folder_path,
    }));
  return {
    id: row.id,
    name: row.title || "Untitled playlist",
    nature: "site",
    createdBy: row.ctbydata?.name ?? row.ctby,
    mtBy: row.mtbydata?.name ?? row.mtby,
    mt: row.mt,
    videoCount: videos.length,
    videos,
    youtubeChannels,
    denmarkChannels,
  };
};

/**
 * Fetch custom playlists for a team. Returns an empty list (so the UI shows its empty state)
 * when no team is supplied or the RPC fails.
 */
export const fetchPlaylists = async (team?: string): Promise<Playlist[]> => {
  if (!team) return [];
  const res = await PlaylistListApi({ team });
  if (res.error || !Array.isArray(res.data)) return [];
  return res.data.map(toPlaylist);
};

export const fetchPlaylistById = async (id: string): Promise<Playlist> => {
  const res = await PlaylistGetApi({ id });
  if (res.error) throw new Error(String(res.error));
  if (!res.data) throw new Error("Playlist not found");
  return toPlaylist(res.data);
};

// Namespace a YouTube-playlist id so it can't collide with a custom-playlist id in the shared
// selection set. The channel is folded in so the same playlist id under two channels stays distinct.
const ytPlaylistId = (channelId: string, playlistId: string) =>
  `yt:${channelId}:${playlistId}`;

// Map one channel's YouTube playlist into the UI's Playlist shape ("both" = YouTube-backed).
const toYoutubePlaylist = (
  pl: YoutubePlaylist,
  channel: SocialChannel
): Playlist => ({
  id: ytPlaylistId(channel.channel || channel.id, pl.id),
  name: pl.title || "Untitled playlist",
  nature: "both",
  channelId: channel.channel || channel.id,
  channelName: channel.name,
});

/**
 * Fetch a single connected YouTube channel's playlists — the same `youtubeplaylist.get` source
 * the Social Publish add-playlist dropdown uses — mapped into the UI's Playlist shape. Called
 * directly (not via the shared Redux getYoutubePlaylist, which holds only one channel at a time
 * and would clobber the Social Publish card's state).
 */
export const fetchChannelYoutubePlaylists = async (
  channel: SocialChannel
): Promise<Playlist[]> => {
  const channelKey = channel.channel || channel.id;
  if (!channelKey) return [];
  const res = await GetYoutubePlaylistAPI(channelKey);
  if (res.error || !Array.isArray(res.data)) return [];
  return res.data.map((pl) => toYoutubePlaylist(pl, channel));
};
